// src/utils/staffGroupUtils.js
import { collection, getDocs, doc, getDoc, writeBatch } from "firebase/firestore";
import { db } from "../firebase";
import { OrganizationService } from "../services/organizationService";
import dayjs from "dayjs";

/**
 * スタッフをスキルと経験に基づいてグループに分類（組織ベース対応）
 * @param {Array} staffList - スタッフリスト
 * @param {string} organizationId - 組織ID
 * @returns {Object} スタッフグループ
 */
export const categorizeStaffIntoGroups = async (staffList, organizationId) => {
  const staffGroups = {};
  
  try {
    if (!organizationId) {
      console.error("組織IDが指定されていません");
      return staffGroups;
    }

    // 組織配下のスキルグループを取得
    const groupsCollection = OrganizationService.getOrgCollection(
      organizationId,
      "skillGroups"
    );
    const groupsSnap = await getDocs(groupsCollection);
    const groups = groupsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // 結果構造の初期化
    staffList.forEach(staff => {
      staffGroups[staff.employeeId] = [];
    });
    
    // スタッフをグループに割り当て
    groups.forEach(group => {
      if (group.members && Array.isArray(group.members)) {
        group.members.forEach(empId => {
          if (staffGroups[empId]) {
            staffGroups[empId].push(group.id);
          }
        });
      }
    });
  } catch (error) {
    console.error("スタッフのグループ分類エラー:", error);
  }
  
  return staffGroups;
};

/**
 * 割り当てに使用するスタッフスキルサマリーを生成
 * @param {Object} proceduresByDept - 診療科ごとの術式
 * @param {number} combatPower - 戦闘力
 * @param {number} staffYear - スタッフの入職年
 * @returns {Object} スキルサマリー
 */
export const generateStaffSkillSummary = (proceduresByDept, combatPower, staffYear) => {
  const summary = {
    combatPower: combatPower || 0,
    year: staffYear || new Date().getFullYear(),
    departments: {},
    procedures: {},
    lastUpdated: new Date().toISOString()
  };
  
  // 診療科レベルのスキルを計算
  for (const dept in proceduresByDept) {
    const procs = proceduresByDept[dept];
    
    // 空の診療科はスキップ
    if (!procs || procs.length === 0) continue;
    
    // この診療科のスキルレベルを計算
    const scrubLevels = procs.map(p => getLevelValue(p.scrub)).filter(v => v > 0);
    const circulatingLevels = procs.map(p => getLevelValue(p.circulating)).filter(v => v > 0);
    
    // 平均レベルと資格率を計算
    const scrubAvg = scrubLevels.length > 0 ? 
      scrubLevels.reduce((a, b) => a + b, 0) / scrubLevels.length : 0;
    const circulatingAvg = circulatingLevels.length > 0 ? 
      circulatingLevels.reduce((a, b) => a + b, 0) / circulatingLevels.length : 0;
    
    const scrubQualified = scrubLevels.filter(l => l >= 6).length / procs.length;
    const circulatingQualified = circulatingLevels.filter(l => l >= 6).length / procs.length;
    
    summary.departments[dept] = {
      scrubAvg: scrubAvg,
      circulatingAvg: circulatingAvg,
      scrubQualifiedRatio: scrubQualified,
      circulatingQualifiedRatio: circulatingQualified
    };
  }
  
  // 特定の術式スキルを収集
  for (const dept in proceduresByDept) {
    const procs = proceduresByDept[dept];
    
    procs.forEach(proc => {
      summary.procedures[proc.id] = {
        name: proc.name,
        department: dept,
        scrubLevel: getLevelValue(proc.scrub),
        circulatingLevel: getLevelValue(proc.circulating)
      };
    });
  }
  
  return summary;
};

/**
 * 全スタッフのスキルサマリーを生成して保存する（組織ベース対応）
 * @param {Array} staffList - スタッフリスト
 * @param {string} organizationId - 組織ID
 * @param {Object} proceduresByDept - 診療科ごとの術式データ（オプション）
 * @param {Object} combatPowerByStaff - スタッフごとの戦闘力（オプション）
 * @returns {Promise<Object>} - 保存されたスキルサマリー
 */
export const generateAndSaveStaffSkillSummaries = async (
  staffList, 
  organizationId,
  proceduresByDept = null, 
  combatPowerByStaff = {}
) => {
  if (!staffList || staffList.length === 0) {
    console.error("スタッフリストが空です");
    return {};
  }

  if (!organizationId) {
    console.error("組織IDが指定されていません");
    return {};
  }
  
  try {
    // 組織配下のスキルレコードを取得
    const skillRecordsCollection = OrganizationService.getOrgCollection(
      organizationId,
      "skillRecords"
    );
    const skillRecordsSnap = await getDocs(skillRecordsCollection);
    const skillRecords = skillRecordsSnap.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    }));
    
    // 組織配下の術式マスタを取得
    const proceduresCollection = OrganizationService.getOrgCollection(
      organizationId,
      "procedures"
    );
    const proceduresSnap = await getDocs(proceduresCollection);
    const procedures = proceduresSnap.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    }));
    
    // 組織配下の診療科マスタを取得
    const departmentsCollection = OrganizationService.getOrgCollection(
      organizationId,
      "departments"
    );
    const departmentsSnap = await getDocs(departmentsCollection);
    const departments = departmentsSnap.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    }));
    
    // 各スタッフのスキルデータを整形
    const staffSkillData = {};
    
    staffList.forEach(staff => {
      const staffId = staff.id;
      const yearJoined = parseInt(staff.year) || new Date().getFullYear();
      
      // スタッフごとの診療科別術式データの整形
      const staffProcByDept = {};
      
      // デフォルトの診療科データ構造を作成
      departments.forEach(dept => {
        const relatedProcs = procedures.filter(p => p.departmentId === dept.id);
        
        staffProcByDept[dept.name] = relatedProcs.map(proc => {
          // この術式のスキルレコードを取得
          const scrubRecord = skillRecords.find(
            r => r.userId === staffId && 
                 r.procedureId === proc.id && 
                 r.role === "scrub"
          );
          
          const circulatingRecord = skillRecords.find(
            r => r.userId === staffId && 
                 r.procedureId === proc.id && 
                 r.role === "circulating"
          );
          
          return {
            id: proc.id,
            name: proc.name,
            departmentName: dept.name,
            scrub: scrubRecord?.level || "未経験",
            circulating: circulatingRecord?.level || "未経験",
            scrubId: scrubRecord?.id,
            circulatingId: circulatingRecord?.id,
          };
        });
      });
      
      // 戦闘力を取得または計算
      const combatPower = combatPowerByStaff[staffId] || 
                          calculateCombatPower(staffProcByDept);
      
      // スキルサマリーを生成
      staffSkillData[staffId] = generateStaffSkillSummary(
        staffProcByDept, 
        combatPower, 
        yearJoined
      );
    });
    
    return staffSkillData;
  } catch (error) {
    console.error("スキルサマリー生成エラー:", error);
    return {};
  }
};

/**
 * 戦闘力を計算する
 * @param {Object} proceduresByDept - 診療科ごとの術式データ
 * @returns {number} 戦闘力
 */
const calculateCombatPower = (proceduresByDept) => {
  let totalPower = 0;
  let totalProcedures = 0;
  
  for (const dept in proceduresByDept) {
    const procs = proceduresByDept[dept];
    
    procs.forEach(proc => {
      const scrubLevel = getLevelValue(proc.scrub);
      const circulatingLevel = getLevelValue(proc.circulating);
      
      // 各術式の戦闘力は器械出しと外回りの平均
      totalPower += (scrubLevel + circulatingLevel) / 2;
      totalProcedures++;
    });
  }
  
  // 全術式の平均戦闘力を返す
  return totalProcedures > 0 ? Math.round(totalPower / totalProcedures * 100) / 100 : 0;
};

/**
 * スキルレベルを数値に変換
 * @param {string} level - スキルレベル文字列
 * @returns {number} レベル値
 */
const getLevelValue = (level) => {
  const levelMap = {
    "未経験": 0,
    "見学済み": 1,
    "経験1回": 2,
    "経験2回": 3,
    "経験3回以上": 4,
    "外から見守り": 5,
    "独り立ち": 6,
    "指導可": 7
  };
  
  return levelMap[level] || 0;
};

/**
 * スタッフの可用性をチェック
 * @param {Object} staff - スタッフオブジェクト
 * @param {string} date - 日付（YYYY-MM-DD形式）
 * @param {Array} existingAssignments - 既存の割り当て
 * @returns {boolean} 可用かどうか
 */
export const isStaffAvailable = (staff, date, existingAssignments = []) => {
  // この日に既に割り当てられているかチェック
  const hasAssignment = existingAssignments.some(
    assignment => assignment.staffId === staff.id && assignment.date === date
  );
  
  return !hasAssignment;
};

/**
 * スキルグループに基づいてスタッフをフィルタリング
 * @param {Array} staffList - スタッフリスト
 * @param {string} groupId - スキルグループID
 * @param {string} organizationId - 組織ID
 * @returns {Promise<Array>} フィルタリングされたスタッフリスト
 */
export const filterStaffByGroup = async (staffList, groupId, organizationId) => {
  if (!groupId || !organizationId) {
    return staffList;
  }
  
  try {
    const groupRef = OrganizationService.getOrgDoc(
      organizationId,
      "skillGroups",
      groupId
    );
    const groupDoc = await getDoc(groupRef);
    
    if (!groupDoc.exists()) {
      console.error("スキルグループが見つかりません");
      return staffList;
    }
    
    const groupData = groupDoc.data();
    const memberIds = groupData.members || [];
    
    return staffList.filter(staff => memberIds.includes(staff.employeeId));
  } catch (error) {
    console.error("グループフィルタリングエラー:", error);
    return staffList;
  }
};